import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MergeAnalysisService } from '@/services/merge-bots/merge-analysis-service';
import { findLinkedBotInfo } from '@/services/shadow-bot-utils';
import { normalizeInstrument, createInstrumentMapping, getChildBotIds } from '@/utils/instrument-utils';
import { createChildSettingsMap } from '@/utils/child-settings-utils';
import type { MergedTradeHistoryItem, TradingHistoryWithSource } from '@/services/prop-bot/prop-bot-trade-history-service';
import type { PropBotGrowthData, GrowthDataPoint } from '@/services/prop-bot/prop-bot-growth-service';

interface BotSignalEvent {
  id: string;
  bot_id: string;
  action: string;
  action_type: string;
  instrument: string;
  amount: number;
  timestamp: string;
  win: boolean;
  created_at: string;
  updated_at: string;
}

interface RealtimeConfig {
  childBotIds: string[];
  allowedBotIds: string[];
  instrumentMap: Map<string, { childBotId: string; childBotName?: string }>;
  childSettingsMap: Map<string, { tp_usd: number; sl_usd: number; quantity: number }>;
  propSettings: {
    winPercentage: number;
    lossPercentage: number;
    minCapital: number;
  };
}

export const useRealtimePropBot = (botId: string) => {
  const queryClient = useQueryClient();
  const processedEventsRef = useRef(new Set<string>());
  const configRef = useRef<RealtimeConfig | null>(null);

  // Initialize realtime configuration
  const initializeConfig = useCallback(async () => {
    try {
      console.log('Initializing realtime config for botId:', botId);
      
      // Priority 1: Try current bot
      let mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(botId);
      
      // Priority 2: Try linked bot
      if (!mergedRun) {
        const linkedInfo = await findLinkedBotInfo(botId);
        if (linkedInfo.linkedBotId) {
          mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(linkedInfo.linkedBotId);
        }
      }
      
      // Priority 3: Check bot_merge_mapping for current bot as child
      if (!mergedRun) {
        try {
          const { data: mapping } = await supabase
            .from('bot_merge_mapping')
            .select('parent_bot_id')
            .eq('child_bot_id', botId)
            .eq('is_active', true)
            .single();
          
          if (mapping?.parent_bot_id) {
            mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
          }
        } catch (error) {
          // Ignore mapping lookup errors
        }
      }
      
      // Priority 4: Check bot_merge_mapping for linked bot as child
      if (!mergedRun) {
        const linkedInfo = await findLinkedBotInfo(botId);
        if (linkedInfo.linkedBotId) {
          try {
            const { data: mapping } = await supabase
              .from('bot_merge_mapping')
              .select('parent_bot_id')
              .eq('child_bot_id', linkedInfo.linkedBotId)
              .eq('is_active', true)
              .single();
            
            if (mapping?.parent_bot_id) {
              mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
            }
          } catch (error) {
            // Ignore mapping lookup errors
          }
        }
      }
      
      if (!mergedRun?.config) {
        console.log('No merged run with config found for realtime updates');
        return null;
      }
      
      // Get child bot IDs, instrument mapping, and child settings map
      const childBotIds = getChildBotIds(mergedRun.config, mergedRun.child_bot_ids);
      const instrumentMap = createInstrumentMapping(mergedRun.config);
      const childSettingsMap = createChildSettingsMap(mergedRun.config);
      
      // Create allowed bot IDs list (child bots + current bot + linked bot)
      const allowedBotIds = [...childBotIds];
      if (!allowedBotIds.includes(botId)) {
        allowedBotIds.push(botId);
      }
      const linkedInfo = await findLinkedBotInfo(botId);
      if (linkedInfo.linkedBotId && !allowedBotIds.includes(linkedInfo.linkedBotId)) {
        allowedBotIds.push(linkedInfo.linkedBotId);
      }
      
      // Get prop settings
      const originalBotId = linkedInfo.currentBotType === 'shadow' ? linkedInfo.linkedBotId : botId;
      const queryBotId = originalBotId || botId;

      const { data: bot } = await supabase
        .from('bots')
        .select(`
          propDetails:bot_prop_details!bot_prop_details_bot_id_fkey(
            win_percentage,
            loss_percentage,
            min_capital
          )
        `)
        .eq('id', queryBotId)
        .eq('type', 'prop_bot')
        .single();

      const propSettings = {
        winPercentage: bot?.propDetails?.[0]?.win_percentage ?? 2.0,
        lossPercentage: bot?.propDetails?.[0]?.loss_percentage ?? 1.5,
        minCapital: Number(bot?.propDetails?.[0]?.min_capital) ?? 100000
      };
      
      const config: RealtimeConfig = {
        childBotIds,
        allowedBotIds,
        instrumentMap,
        childSettingsMap,
        propSettings
      };
      
      configRef.current = config;
      console.log('Realtime config initialized:', {
        childBotIds: config.childBotIds,
        allowedBotIds: config.allowedBotIds,
        instruments: Array.from(config.instrumentMap.keys()),
        propSettings: config.propSettings
      });
      
      return config;
    } catch (error) {
      console.error('Error initializing realtime config:', error);
      return null;
    }
  }, [botId]);

  // Handle realtime bot signal events
  const handleBotSignalEvent = useCallback((payload: any) => {
    const event = payload.new as BotSignalEvent;
    
    // Skip if already processed
    if (processedEventsRef.current.has(event.id)) {
      return;
    }
    
    // Skip if not EXIT action
    if (!['EXIT_LONG', 'EXIT_SHORT'].includes(event.action)) {
      return;
    }
    
    const config = configRef.current;
    if (!config) {
      return;
    }
    
    // Skip if bot not in our allowed list
    if (!config.allowedBotIds.includes(event.bot_id)) {
      console.log('Skipping realtime event from non-allowed bot:', event.bot_id, 'Allowed bots:', config.allowedBotIds);
      return;
    }
    
    const normalizedInstrument = normalizeInstrument(event.instrument);
    
    // Skip if instrument not mapped to our merged analysis (relax if map is empty)
    if (config.instrumentMap.size > 0) {
      const fallbackInstrument = normalizedInstrument.replace(/USD/g, '');
      const hasMapping = config.instrumentMap.has(normalizedInstrument) || 
                        (fallbackInstrument && config.instrumentMap.has(fallbackInstrument));
      
      if (!hasMapping) {
        console.log('Skipping realtime event with unmapped instrument:', event.instrument, 'Available instruments:', Array.from(config.instrumentMap.keys()));
        return;
      }
    }
    
    console.log('Processing realtime bot signal event:', {
      id: event.id,
      action: event.action,
      instrument: event.instrument,
      normalizedInstrument,
      win: event.win,
      timestamp: event.timestamp
    });
    
    // Mark as processed
    processedEventsRef.current.add(event.id);
    
    // Update Trading History cache
    queryClient.setQueryData<TradingHistoryWithSource>(
      ['propMergedTradingHistory', botId],
      (oldData) => {
        if (!oldData) return oldData;
        
        const winValue = event.win;
        const isWin = winValue === true || (typeof winValue === 'number' && winValue === 1) || (typeof winValue === 'string' && winValue === '1');
        
        // Get child bot settings for this instrument or bot
        const mappingInfo = config.instrumentMap.get(normalizedInstrument) || config.instrumentMap.get(normalizedInstrument.replace(/USD/g, ''));
        const childBotId = mappingInfo?.childBotId || event.bot_id;
        const childSettings = config.childSettingsMap.get(childBotId);
        
        // Calculate normalized P&L using TP/SL and quantity
        let normalized_pnl = 0;
        if (childSettings && event.amount) {
          const coefficient = event.amount / childSettings.quantity;
          normalized_pnl = isWin 
            ? coefficient * childSettings.tp_usd
            : -coefficient * childSettings.sl_usd;
        } else {
          // Fallback to prop settings calculation
          const pnlPercentage = isWin ? config.propSettings.winPercentage : config.propSettings.lossPercentage;
          normalized_pnl = isWin 
            ? (pnlPercentage / 100) * config.propSettings.minCapital
            : -(pnlPercentage / 100) * config.propSettings.minCapital;
        }
        
        const newTradeItem: MergedTradeHistoryItem = {
          timestamp: event.timestamp,
          instrument: normalizedInstrument,
          amount: event.amount || 0,
          action: event.action === 'EXIT_LONG' ? 'LONG' : 'SHORT',
          normalized_pnl
        };
        
        // Check for duplicates
        const isDuplicate = oldData.trades.some(trade => 
          trade.timestamp === newTradeItem.timestamp &&
          trade.instrument === newTradeItem.instrument &&
          trade.action === newTradeItem.action &&
          Math.abs(trade.amount - newTradeItem.amount) < 0.01
        );
        
        if (isDuplicate) {
          return oldData;
        }
        
        const updatedTrades = [newTradeItem, ...oldData.trades];
        
        // Sort by timestamp descending (newest first) and limit to prevent memory bloat
        updatedTrades.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        const limitedTrades = updatedTrades.slice(0, 500); // Keep last 500 trades
        
        console.log('Updated trading history cache with realtime trade');
        
        return {
          ...oldData,
          trades: limitedTrades
        };
      }
    );
    
    // Update Growth Chart cache
    queryClient.setQueryData<PropBotGrowthData>(
      ['propBotGrowth', botId],
      (oldData) => {
        if (!oldData?.merged?.growthData) return oldData;
        
        const lastPoint = oldData.merged.growthData[oldData.merged.growthData.length - 1];
        if (!lastPoint) return oldData;
        
        const winValue = event.win;
        const isWin = winValue === true || (typeof winValue === 'number' && winValue === 1) || (typeof winValue === 'string' && winValue === '1');
        const change = isWin ? 1 : -1;
        
        // Get child bot settings for this instrument or bot
        const mappingInfo = config.instrumentMap.get(normalizedInstrument) || config.instrumentMap.get(normalizedInstrument.replace(/USD/g, ''));
        const childBotId = mappingInfo?.childBotId || event.bot_id;
        const childSettings = config.childSettingsMap.get(childBotId);
        
        // Calculate dollar change using TP/SL and quantity
        let dollarChange = 0;
        if (childSettings && event.amount) {
          const coefficient = event.amount / childSettings.quantity;
          dollarChange = isWin 
            ? coefficient * childSettings.tp_usd
            : -coefficient * childSettings.sl_usd;
        } else {
          // Fallback to prop settings calculation
          dollarChange = isWin 
            ? (config.propSettings.winPercentage / 100) * config.propSettings.minCapital
            : -(config.propSettings.lossPercentage / 100) * config.propSettings.minCapital;
        }
        
        const newGrowthPoint: GrowthDataPoint = {
          tradeNumber: lastPoint.tradeNumber + 1,
          growth: lastPoint.cumulative + change,
          dollarAmount: lastPoint.cumulativeDollar + dollarChange,
          prediction: isWin ? 'WIN' : 'LOSS',
          actual: isWin ? 1 : 0,
          change,
          isCorrect: true,
          cumulative: lastPoint.cumulative + change,
          cumulativeDollar: lastPoint.cumulativeDollar + dollarChange,
          source: 'realtime',
          timestamp: event.timestamp,
          instrument: normalizedInstrument
        };
        
        const updatedGrowthData = [...oldData.merged.growthData, newGrowthPoint];
        
        // Limit growth data to prevent memory bloat
        const limitedGrowthData = updatedGrowthData.slice(-500); // Keep last 500 points
        
        // Recalculate stats
        const finalPoint = limitedGrowthData[limitedGrowthData.length - 1];
        const updatedStats = {
          ...oldData.merged.stats,
          finalGrowth: finalPoint.cumulative,
          finalDollarAmount: finalPoint.cumulativeDollar,
          totalTrades: limitedGrowthData.length,
          finalProfit: finalPoint.cumulativeDollar
        };
        
        console.log('Updated growth chart cache with realtime point');
        
        return {
          ...oldData,
          merged: {
            ...oldData.merged,
            growthData: limitedGrowthData,
            stats: updatedStats
          }
        };
      }
    );
  }, [botId, queryClient]);

  // Set up realtime subscription
  useEffect(() => {
    let channel: any = null;
    
    const setupRealtime = async () => {
      const config = await initializeConfig();
      if (!config || config.allowedBotIds.length === 0) {
        console.log('No allowed bots found for realtime updates');
        return;
      }
      
      console.log('Setting up realtime subscription for allowed bots:', config.allowedBotIds);
      
      channel = supabase
        .channel('prop-bot-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'bot_signal_events'
          },
          handleBotSignalEvent
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'bot_signal_events'
          },
          handleBotSignalEvent
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
        });
    };
    
    if (botId) {
      setupRealtime();
    }
    
    return () => {
      if (channel) {
        console.log('Cleaning up realtime subscription');
        supabase.removeChannel(channel);
      }
    };
  }, [botId, initializeConfig, handleBotSignalEvent]);

  // Clear processed events when botId changes
  useEffect(() => {
    processedEventsRef.current.clear();
    configRef.current = null;
  }, [botId]);
};